import { Pagination } from '@/components/Pagination';
import { ThEl } from '@/features/Quiz/components/ArticlesQuiz';
import { useLibraryQuizListV2 } from '@/features/Quiz/api/libraryQuizListV2';
import type { LibraryQuizListResultV2 } from '@/features/Quiz/api/libraryQuizListV2';
import type { VideoQuizDomainV2 } from '@/features/Quiz/api/getVideoQuizDomains';
import { ArticleDomainFilter } from '@/features/Quiz/components/ArticleDomainFilter';
import { LocaleFilter } from '@/features/Quiz/components/LocaleFilter';
import { DeleteQuizModal } from '@/features/Quiz/components/DeleteQuizModal';
import { debounce } from '@/utils/helpers';
import {
    Box,
    Button,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Spinner as SpinnerChakra,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaSearch } from 'react-icons/fa';
import { HiX } from 'react-icons/hi';
import { format } from 'date-fns';
import InstitutionsModal from '@/features/Playlists/components/InstitutionsModal';
import { useAdminQuizV2Cc } from '@/api/ccApi';
import { InstitutionModalTypes } from '@/features/Playlists';
import { useAdminQuizV2Scorm } from '@/api/scormApi';

const PAGE_SIZE = 20;

type SearchFormData = { keyword: string };

const ArticlesQuizItemV2 = ({ result }: { result: LibraryQuizListResultV2 }) => {
    const quizUuid = result.quiz.uuid;
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isInstitutionsOpen, onOpen: onInstitutionsOpen, onClose: onInstitutionsClose } = useDisclosure();
    const [type, setType] = useState<InstitutionModalTypes>(null);

    const { isLoading: isCCLoading, handleCCDownload } = useAdminQuizV2Cc({
        quizUuid,
        title: result.quiz.title ?? '',
    });

    const { isLoading: isScormLoading, handleScormDownload } = useAdminQuizV2Scorm({
        quizUuid,
        playlistTitle: result.quiz.title ?? '',
    });

    const handleOpenInstitutionsCC = () => {
        setType('cc');
        onInstitutionsOpen();
    };

    const handleOpenInstitutionsScorm = () => {
        setType('scorm');
        onInstitutionsOpen();
    };

    return (
        <>
            <InstitutionsModal
                isOpen={isInstitutionsOpen}
                onClose={onInstitutionsClose}
                handleQtiDownload={() => undefined}
                handleScormDownload={handleScormDownload}
                handleCCDownload={handleCCDownload}
                isLoading={isScormLoading || isCCLoading}
                type={type}
            />
            <DeleteQuizModal
                isOpen={isOpen}
                onClose={onClose}
                quiz={{ quizUuid, title: result.quiz.title ?? '' }}
                quizType="libraryV2"
            />
            {/* @ts-expect-error Expression produces a union type that is too complex to represent. */}
            <Tr>
                <Td w="65%" py="10px">
                    <Flex as={Link} py="10px" to={`/quiz/view/${quizUuid}`}>
                        <Text color="black" lineHeight="20px">
                            {result.quiz.title}
                        </Text>
                    </Flex>
                </Td>
                <Td textAlign="center" w="10%">
                    <Text color="black" lineHeight="21px" textAlign="center" display="inline-block" p="0 10px" w="auto">
                        {result.quiz.created_at
                            ? format(new Date(result.quiz.created_at), 'MM/dd/yyyy')
                            : '-'}
                    </Text>
                </Td>
                <Td textAlign="center" w="15%">
                    <Text color="black" lineHeight="21px" textAlign="center" display="inline-block" p="0 10px" w="auto">
                        {result.quiz.no_of_questions != null
                            ? `${result.quiz.no_of_questions} Questions`
                            : '-'}
                    </Text>
                </Td>
                <Td w="auto" maxWidth="260px">
                    <Flex columnGap="10px" align="center" justifyContent="space-around">
                        <Box as={Link} to={`/quiz/edit/${quizUuid}`} borderRadius="16px" height="28px">
                            <Tooltip hasArrow label="Edit quiz" aria-label="Edit quiz">
                                <span>
                                    <Icon highlightOnHover icon="pencil" />
                                </span>
                            </Tooltip>
                        </Box>

                           {/* Copy Quiz action intentionally hidden.
                            Duplicating a quiz offers limited value since it copies the same questions/videos.
                            Additionally, video quizzes do not support multiple quizzes for the same video. */}
                            {/* <Box as={Link} to={`/quiz/copy/${quizUuid}`} borderRadius="16px" height="28px">
                                <Tooltip hasArrow label="Copy quiz" aria-label="Copy quiz">
                                    <span>
                                        <Icon highlightOnHover icon="clone" />
                                    </span>
                                </Tooltip>
                            </Box> */}

                        <Box onClick={handleOpenInstitutionsScorm}>
                            <Tooltip hasArrow label="Download SCORM" aria-label="Download SCORM">
                                {isScormLoading ? (
                                    <Spinner size="xs" />
                                ) : (
                                    <span>
                                        <Icon highlightOnHover icon="file-zipper" />
                                    </span>
                                )}
                            </Tooltip>
                        </Box>

                        <Box onClick={handleOpenInstitutionsCC}>
                            <Tooltip hasArrow label="Download CC" aria-label="Download CC">
                                {isCCLoading ? (
                                    <Spinner size="xs" />
                                ) : (
                                    <span>
                                        <Icon fontSize="20px" highlightOnHover icon="closed-captioning" />
                                    </span>
                                )}
                            </Tooltip>
                        </Box>

                        <Box onClick={onOpen} fontSize="0.875rem" color="#cc0000" fontWeight={500} cursor="pointer">
                            <Tooltip hasArrow label="Delete quiz" aria-label="Delete quiz">
                                <span>
                                    <Icon highlightOnHover icon="trash" />
                                </span>
                            </Tooltip>
                        </Box>
                    </Flex>
                </Td>
            </Tr>
        </>
    );
};

export const ArticlesQuizV2 = () => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [selectedDomain, setSelectedDomain] = useState<VideoQuizDomainV2 | null>(null);
    const [selectedLocale, setSelectedLocale] = useState('');

    const { register, reset, watch } = useForm<SearchFormData>({
        defaultValues: { keyword: '' },
    });

    const { data, isFetching } = useLibraryQuizListV2({
        page,
        size: PAGE_SIZE,
        search: search || undefined,
        filters: {
            libraryType: 'VIDEO',
            ...(selectedDomain ? { joveDomainId: selectedDomain.id } : {}),
            ...(selectedLocale ? { locale: selectedLocale } : {}),
        },
        sort: { field: 'created_at', direction: 'desc' },
    });

    const results = data?.results ?? [];
    const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

    const applySearch = useCallback((keyword: string) => {
        setPage(1);
        setSearch(keyword);
    }, []);

    const onSearchChange = debounce(
        useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                applySearch(e.target.value);
            },
            [applySearch]
        ),
        300
    );

    const handleReset = useCallback(() => {
        reset();
        applySearch('');
    }, [reset, applySearch]);

    const handleDomainChange = useCallback((domain: VideoQuizDomainV2 | null) => {
        setPage(1);
        setSelectedDomain(domain);
    }, []);

    const handleLocaleChange = useCallback((locale: string) => {
        setPage(1);
        setSelectedLocale(locale);
    }, []);

    return (
        <Flex w="100%" flexDir="column" mb="10px">
            <Flex gap="20px" sx={{ '.btn-half-width': { flex: '1 0 45%' } }} w="100%">
                <Flex justifyContent="space-between" alignItems="center" mb={3} w="100%">
                    <Flex gap="12px" alignItems="center" flex="1" minW={0} mr="12px">
                        <InputGroup minW="280px" maxW="400px" flex="1">
                            <InputLeftElement className="InputLeft" pointerEvents="auto">
                                {isFetching ? <SpinnerChakra size="20px" /> : <FaSearch size="20px" />}
                            </InputLeftElement>
                            <Input
                                placeholder="Search by Title"
                                bg="#ffffff"
                                id="keyword"
                                type="text"
                                borderRadius="1000px"
                                {...register('keyword')}
                                onChange={onSearchChange}
                            />
                            {watch('keyword') && (
                                <InputRightElement cursor="pointer" onClick={handleReset}>
                                    <HiX />
                                </InputRightElement>
                            )}
                        </InputGroup>
                        <ArticleDomainFilter value={selectedDomain} onChange={handleDomainChange} />
                        <LocaleFilter value={selectedLocale} onChange={handleLocaleChange} />
                    </Flex>
                    <Flex>
                        <Button
                            _focus={{ outline: 0 }}
                            _hover={{}}
                            _active={{}}
                            as={Link}
                            to="/quiz/create?libraryType=VIDEO"
                            variant="brand"
                            cursor="pointer">
                            <Icon icon="plus" /> &nbsp; Create a New Quiz
                        </Button>
                    </Flex>
                </Flex>
            </Flex>

            <Flex flexDir="column">
                <Table bgColor="#ffffff">
                    {results.length > 0 && (
                        <Thead borderBottom="1px solid #e5ebf3">
                            <Tr w="100%">
                                <ThEl align="start" w="65%">Title</ThEl>
                                <ThEl w="10%">Date Created</ThEl>
                                <ThEl w="15%">Questions</ThEl>
                                <ThEl w="15%">Actions</ThEl>
                            </Tr>
                        </Thead>
                    )}
                    <Tbody>
                        {results.length > 0 ? (
                            results.map((result) => (
                                <ArticlesQuizItemV2 key={result.id} result={result} />
                            ))
                        ) : (
                            <>
                                {!isFetching && (
                                    <Tr>
                                        <Td>
                                            <Box>No list items</Box>
                                        </Td>
                                    </Tr>
                                )}
                            </>
                        )}
                    </Tbody>
                </Table>

                {totalPages > 1 && (
                    <Flex justifyContent="end" mt="20px">
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            handlePagination={setPage}
                        />
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};
